import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getAppVersionsLink,
  getAuthenticatedSelector,
  getBootstrapLoadingState,
} from 'app/store/Auth/selectors';
import axios from 'axios';
import useInterval from 'use-interval';
import { config } from 'config';
import { channels } from 'shared/constants';
import { Link } from 'types/api.types';
import { useEffect, useState } from 'react';
import { FetchAppVersionsResponse } from 'types/bootstrap';
import { LoadingState } from 'types/enums';
import {
  getAllSubscriptionQuotas,
  getCheckSubscriptionLink,
  getSubscriptionQuotasLink,
  getSubscriptionTiersDDLLink,
  getTierListLink,
} from 'app/store/Subscription/selectors';
import { fetchStorageEffect } from 'app/store/Storage/effects';
import {
  checkSubscriptionEffect,
  fetchSubscriptionQuotasEffect,
  fetchSubscriptionTierListEffect,
  fetchSubscriptionTiersDDLEffect,
} from 'app/store/Subscription/effects';
import { getStorageLink } from 'app/store/Storage/selectors';
import { fetchIntercomConfigEffect } from 'app/store/Intercom/effects';
import {
  getIntercomConfig,
  getIntercomConfigLink,
  getIntercomLoadingState,
} from 'app/store/Intercom/selectors';

const INTERVAL = 1000 * 60 * 30; // 30 minutes

export const useSetMinimumAppVersion = (bootstrapLoading: boolean) => {
  const [
    previousMinimumVersion,
    setPreviousMinimumVersion,
  ] = useState<string>();
  const appVersionsLink = useAppSelector(getAppVersionsLink);
  const shouldPoll = !bootstrapLoading && config.isElectronBuild;

  const fetchAppVersions = async (appVersionsLink?: Link) => {
    if (appVersionsLink) {
      const response = await axios.get<FetchAppVersionsResponse>(
        appVersionsLink?.href,
        {
          withCredentials: true,
        }
      );
      const minimumAppVersion =
        response.data.response.appVersions?.['session-client-studio-desktop']
          .minimumVersion;
      setPreviousMinimumVersion(minimumAppVersion);
      if (previousMinimumVersion !== minimumAppVersion) {
        window.api.send(channels.SET_MINIMUM_APP_VERSION, minimumAppVersion);
      }
    }
  };

  useEffect(() => {
    if (shouldPoll) {
      fetchAppVersions(appVersionsLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appVersionsLink, shouldPoll]);

  useInterval(
    () => fetchAppVersions(appVersionsLink),
    shouldPoll ? INTERVAL : null,
    true
  );
};

export const useFetchStorageData = () => {
  const dispatch = useAppDispatch();
  const storage = useAppSelector(getAllSubscriptionQuotas);
  const isAuthenticated = useAppSelector(getAuthenticatedSelector);
  const bootstrapLoadingState = useAppSelector(getBootstrapLoadingState);
  const storageLink = useAppSelector(getStorageLink);

  useEffect(() => {
    if (
      bootstrapLoadingState === LoadingState.FETCHED &&
      isAuthenticated &&
      !storage &&
      storageLink
    ) {
      dispatch(fetchStorageEffect());
    }
  }, [dispatch, storage, bootstrapLoadingState, storageLink, isAuthenticated]);
};

export const useFetchSubscriptionsData = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(getAuthenticatedSelector);
  const bootstrapLoadingState = useAppSelector(getBootstrapLoadingState);
  const tierListLink = useAppSelector(getTierListLink);
  const subscriptionTiersDDLLink = useAppSelector(getSubscriptionTiersDDLLink);
  const checkSubscriptionLink = useAppSelector(getCheckSubscriptionLink);
  const subscriptionQuotasLink = useAppSelector(getSubscriptionQuotasLink);

  const isBootstrapped =
    bootstrapLoadingState === LoadingState.FETCHED && isAuthenticated;

  useEffect(() => {
    if (isBootstrapped && tierListLink) {
      dispatch(fetchSubscriptionTierListEffect({ link: tierListLink }));
    }
  }, [dispatch, tierListLink, isBootstrapped]);

  useEffect(() => {
    if (isBootstrapped && subscriptionTiersDDLLink) {
      dispatch(
        fetchSubscriptionTiersDDLEffect({ link: subscriptionTiersDDLLink })
      );
    }
  }, [dispatch, subscriptionTiersDDLLink, isBootstrapped]);

  useEffect(() => {
    if (isBootstrapped && checkSubscriptionLink) {
      dispatch(checkSubscriptionEffect());
    }
  }, [dispatch, checkSubscriptionLink, isBootstrapped]);

  useEffect(() => {
    if (isBootstrapped && subscriptionQuotasLink) {
      dispatch(fetchSubscriptionQuotasEffect());
    }
  }, [dispatch, subscriptionQuotasLink, isBootstrapped]);
};

export const useIntercomData = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(getAuthenticatedSelector);
  const intercomConfig = useAppSelector(getIntercomConfig);
  const intercomLoading = useAppSelector(getIntercomLoadingState);
  const intercomConfigLink = useAppSelector(getIntercomConfigLink);

  useEffect(() => {
    if (
      intercomConfigLink &&
      !intercomConfig &&
      intercomLoading !== LoadingState.FETCHING
    ) {
      dispatch(fetchIntercomConfigEffect());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, intercomConfig, isAuthenticated, intercomConfigLink]);
};
